/**
 * Example Recipes
 *
 * Curated recipes that showcase parser capabilities and provide
 * realistic test data for shopping list consolidation.
 */

export interface ExampleRecipe {
  id: string;
  name: string;
  servings: number;
  description: string;
  ingredients: string[];
  category: 'dinner' | 'breakfast' | 'dessert' | 'sides';
}

export const EXAMPLE_RECIPES: ExampleRecipe[] = [
  {
    id: 'classic-spaghetti',
    name: 'Classic Spaghetti & Meatballs',
    servings: 4,
    description: 'Italian comfort food with homemade meatballs',
    category: 'dinner',
    ingredients: [
      '1 lb ground beef',
      '½ cup breadcrumbs',
      '¼ cup grated parmesan',
      '1 egg',
      '2 cloves garlic, minced',
      '1 (28 oz) can crushed tomatoes',
      '1 lb spaghetti',
      '2 tablespoons olive oil',
      'salt to taste',
      'black pepper to taste',
      '1 teaspoon dried oregano',
      '½ cup fresh basil, chopped',
    ],
  },
  {
    id: 'beef-chili',
    name: 'Hearty Beef Chili',
    servings: 8,
    description: 'Spicy comfort food perfect for cold days',
    category: 'dinner',
    ingredients: [
      '2 lbs ground beef',
      '2 (15 oz) cans kidney beans',
      '1 (28 oz) can crushed tomatoes',
      '1 (6 oz) can tomato paste',
      '2 cups diced onions',
      '3-4 cloves garlic, minced',
      '2 tablespoons chili powder',
      '1 tablespoon cumin',
      '1 teaspoon paprika',
      'salt to taste',
      '2 cups beef broth',
      '1 tablespoon olive oil',
    ],
  },
  {
    id: 'chicken-stir-fry',
    name: 'Chicken & Vegetable Stir-Fry',
    servings: 4,
    description: 'Quick Asian-inspired dinner',
    category: 'dinner',
    ingredients: [
      '1 1/2 lbs chicken breast, cubed',
      '3 tablespoons soy sauce',
      '2 tablespoons sesame oil',
      '1 tablespoon cornstarch',
      '2 cups broccoli florets',
      '1 red bell pepper, sliced',
      '1 cup snap peas',
      '2 cloves garlic, minced',
      '1 tablespoon fresh ginger, grated',
      '2 cups cooked rice',
      '2 tablespoons vegetable oil',
      '1-2 teaspoons red pepper flakes',
    ],
  },
  {
    id: 'chocolate-chip-cookies',
    name: 'Chocolate Chip Cookies',
    servings: 24,
    description: 'Classic homemade cookies',
    category: 'dessert',
    ingredients: [
      '2 1/4 cups all-purpose flour',
      '1 teaspoon baking soda',
      '1 teaspoon salt',
      '1 cup unsalted butter, softened',
      '3/4 cup granulated sugar',
      '3/4 cup brown sugar',
      '2 large eggs',
      '2 teaspoons vanilla extract',
      '2 cups chocolate chips',
      '1 cup chopped walnuts',
    ],
  },
  {
    id: 'breakfast-scramble',
    name: 'Veggie Breakfast Scramble',
    servings: 2,
    description: 'Healthy morning protein',
    category: 'breakfast',
    ingredients: [
      '4 large eggs',
      '¼ cup milk',
      '1 tablespoon butter',
      '½ cup diced onions',
      '½ bell pepper, diced',
      '1 cup spinach',
      '¼ cup shredded cheddar',
      'salt to taste',
      'black pepper to taste',
      '2 slices whole wheat bread',
    ],
  },
  {
    id: 'chicken-tacos',
    name: 'Chicken Tacos',
    servings: 6,
    description: 'Mexican Tuesday night favorite',
    category: 'dinner',
    ingredients: [
      '2 lbs chicken thighs',
      '2 tablespoons olive oil',
      '1 tablespoon chili powder',
      '1 teaspoon cumin',
      '1 teaspoon paprika',
      'salt to taste',
      '12 corn tortillas',
      '1 cup diced tomatoes',
      '1/2 cup diced onions',
      '1 cup shredded lettuce',
      '1 cup shredded cheddar',
      '1/2 cup sour cream',
      '1 lime, cut into wedges',
      '1/4 cup fresh cilantro, chopped',
    ],
  },
  {
    id: 'garlic-mashed-potatoes',
    name: 'Garlic Mashed Potatoes',
    servings: 6,
    description: 'Creamy side dish',
    category: 'sides',
    ingredients: [
      '3 lbs russet potatoes',
      '4 cloves garlic',
      '1/2 cup unsalted butter',
      '3/4 cup whole milk',
      '1/2 cup sour cream',
      'salt to taste',
      'black pepper to taste',
      '2 tablespoons fresh chives, chopped',
    ],
  },
  {
    id: 'banana-bread',
    name: 'Classic Banana Bread',
    servings: 12,
    description: 'Moist and delicious',
    category: 'dessert',
    ingredients: [
      '2 cups all-purpose flour',
      '1 teaspoon baking soda',
      '1/4 teaspoon salt',
      '1/2 cup unsalted butter, softened',
      '3/4 cup brown sugar',
      '2 large eggs',
      '2 1/3 cups mashed bananas',
      '1 teaspoon vanilla extract',
      '1/2 cup chopped walnuts',
    ],
  },
  {
    id: 'greek-salad',
    name: 'Greek Salad',
    servings: 4,
    description: 'Fresh Mediterranean side',
    category: 'sides',
    ingredients: [
      '4 cups chopped romaine lettuce',
      '2 cups cherry tomatoes, halved',
      '1 cucumber, diced',
      '½ cup red onion, thinly sliced',
      '1 cup kalamata olives',
      '1 cup crumbled feta cheese',
      '¼ cup olive oil',
      '2 tablespoons red wine vinegar',
      '1 teaspoon dried oregano',
      'salt to taste',
      'black pepper to taste',
    ],
  },
  {
    id: 'thai-curry',
    name: 'Thai Red Curry',
    servings: 4,
    description: 'Creamy coconut curry with vegetables',
    category: 'dinner',
    ingredients: [
      '1½ lbs chicken breast, cubed',
      '1 (14 oz) can coconut milk',
      '2-3 tablespoons red curry paste',
      '1 red bell pepper, sliced',
      '1 cup bamboo shoots',
      '½ cup Thai basil',
      '2 tablespoons fish sauce',
      '1 tablespoon brown sugar',
      '2 cups jasmine rice, cooked',
      '1 lime, cut into wedges',
    ],
  },
  {
    id: 'lasagna',
    name: 'Classic Lasagna',
    servings: 8,
    description: 'Layers of pasta, meat sauce, and cheese',
    category: 'dinner',
    ingredients: [
      '1 lb ground beef',
      '½ lb Italian sausage',
      '1 (28 oz) can crushed tomatoes',
      '1 (6 oz) can tomato paste',
      '3 cloves garlic, minced',
      '1 cup diced onions',
      '2 teaspoons dried basil',
      '1½ teaspoons dried oregano',
      'salt to taste',
      '1 lb lasagna noodles',
      '15 oz ricotta cheese',
      '2 cups shredded mozzarella',
      '½ cup grated parmesan',
      '1 egg',
    ],
  },
  {
    id: 'pancakes',
    name: 'Fluffy Pancakes',
    servings: 4,
    description: 'Classic American breakfast',
    category: 'breakfast',
    ingredients: [
      '2 cups all-purpose flour',
      '2 tablespoons white sugar',
      '2 teaspoons baking powder',
      '½ teaspoon salt',
      '1¾ cups milk',
      '2 large eggs',
      '¼ cup unsalted butter, melted',
      '1 teaspoon vanilla extract',
    ],
  },
  {
    id: 'roasted-veggies',
    name: 'Roasted Root Vegetables',
    servings: 6,
    description: 'Caramelized seasonal vegetables',
    category: 'sides',
    ingredients: [
      '2 lbs russet potatoes, cubed',
      '1 lb carrots, chopped',
      '½ lb parsnips, chopped',
      '1 cup red onion, quartered',
      '3-4 tablespoons olive oil',
      '2 teaspoons dried rosemary',
      '1 teaspoon dried thyme',
      'salt to taste',
      'black pepper to taste',
    ],
  },
  {
    id: 'apple-pie',
    name: 'Classic Apple Pie',
    servings: 8,
    description: 'Traditional American dessert',
    category: 'dessert',
    ingredients: [
      '2¼ cups all-purpose flour',
      '1 teaspoon salt',
      '⅔ cup cold butter',
      '6-8 tablespoons ice water',
      '6 cups sliced apples',
      '¾ cup white sugar',
      '2 tablespoons all-purpose flour',
      '1 teaspoon ground cinnamon',
      '¼ teaspoon ground nutmeg',
      '1 tablespoon lemon juice',
    ],
  },
  {
    id: 'french-toast',
    name: 'Cinnamon French Toast',
    servings: 4,
    description: 'Sweet breakfast classic',
    category: 'breakfast',
    ingredients: [
      '4 large eggs',
      '⅔ cup milk',
      '1 teaspoon vanilla extract',
      '1 teaspoon ground cinnamon',
      'pinch of salt',
      '8 slices bread',
      '2 tablespoons butter',
      'maple syrup to taste',
      'powdered sugar to taste',
    ],
  },
  {
    id: 'beef-stroganoff',
    name: 'Beef Stroganoff',
    servings: 6,
    description: 'Creamy mushroom beef over noodles',
    category: 'dinner',
    ingredients: [
      '1½ lbs beef sirloin, sliced',
      '8 oz mushrooms, sliced',
      '1 cup diced onions',
      '2 cloves garlic, minced',
      '2 tablespoons all-purpose flour',
      '2 cups beef broth',
      '1 cup sour cream',
      '2 tablespoons Dijon mustard',
      '2 tablespoons butter',
      '1 lb egg noodles',
      'salt to taste',
      'black pepper to taste',
      '¼ cup fresh parsley, chopped',
    ],
  },
  {
    id: 'brownies',
    name: 'Fudgy Brownies',
    servings: 16,
    description: 'Rich chocolate brownies',
    category: 'dessert',
    ingredients: [
      '1 cup unsalted butter',
      '2¼ cups white sugar',
      '4 large eggs',
      '1¼ cups cocoa powder',
      '1 teaspoon salt',
      '½ teaspoon baking powder',
      '¾ cup all-purpose flour',
      '2 teaspoons vanilla extract',
      '1 cup chocolate chips',
    ],
  },
  {
    id: 'caesar-salad',
    name: 'Caesar Salad',
    servings: 4,
    description: 'Classic creamy salad with croutons',
    category: 'sides',
    ingredients: [
      '1 large head romaine lettuce',
      '½ cup grated parmesan',
      '2 cups croutons',
      '⅓ cup olive oil',
      '3 tablespoons lemon juice',
      '2 cloves garlic, minced',
      '1 teaspoon Dijon mustard',
      '2 teaspoons Worcestershire sauce',
      '1 egg yolk',
      'salt to taste',
      'black pepper to taste',
    ],
  },
  {
    id: 'fish-tacos',
    name: 'Baja Fish Tacos',
    servings: 4,
    description: 'Fresh fish tacos with slaw',
    category: 'dinner',
    ingredients: [
      '1 lb white fish fillets',
      '½ cup all-purpose flour',
      '1 teaspoon chili powder',
      '½ teaspoon cumin',
      'salt to taste',
      '8 corn tortillas',
      '2 cups shredded cabbage',
      '½ cup sour cream',
      '2 tablespoons lime juice',
      '1 tablespoon olive oil',
      '¼ cup fresh cilantro, chopped',
      '1 avocado, sliced',
    ],
  },
  {
    id: 'lentil-soup',
    name: 'Hearty Lentil Soup',
    servings: 6,
    description: 'Healthy vegetarian soup',
    category: 'dinner',
    ingredients: [
      '1½ cups dried lentils',
      '4 cups vegetable broth',
      '1 (14 oz) can diced tomatoes',
      '2 cups diced onions',
      '2 carrots, chopped',
      '2 celery stalks, chopped',
      '3 cloves garlic, minced',
      '1 teaspoon cumin',
      '½ teaspoon turmeric',
      'salt to taste',
      'black pepper to taste',
      '2 tablespoons olive oil',
      '1 bunch spinach',
    ],
  },
  {
    id: 'oatmeal',
    name: 'Classic Oatmeal',
    servings: 2,
    description: 'Warm breakfast porridge',
    category: 'breakfast',
    ingredients: [
      '1 cup rolled oats',
      '2 cups milk',
      '1 pinch salt',
      '2 tablespoons honey',
      '1 teaspoon cinnamon',
      '½ cup fresh berries',
      '2 tablespoons chopped almonds',
      '1 tablespoon butter',
    ],
  },
  {
    id: 'quiche',
    name: 'Spinach & Cheese Quiche',
    servings: 6,
    description: 'Savory egg pie',
    category: 'breakfast',
    ingredients: [
      '1 pie crust',
      '6 large eggs',
      '1 cup heavy cream',
      '1½ cups shredded cheddar',
      '2 cups spinach, chopped',
      '½ cup diced onions',
      'salt to taste',
      'black pepper to taste',
      '¼ teaspoon nutmeg',
    ],
  },
  {
    id: 'coleslaw',
    name: 'Creamy Coleslaw',
    servings: 8,
    description: 'Crunchy cabbage side dish',
    category: 'sides',
    ingredients: [
      '4 cups shredded cabbage',
      '1 cup shredded carrots',
      '½ cup mayonnaise',
      '2 tablespoons white vinegar',
      '1 tablespoon white sugar',
      '½ teaspoon celery seed',
      'salt to taste',
      'black pepper to taste',
    ],
  },
];

/**
 * Get recipes by category
 */
export function getRecipesByCategory(category: ExampleRecipe['category']): ExampleRecipe[] {
  return EXAMPLE_RECIPES.filter(r => r.category === category);
}

/**
 * Get recipe by ID
 */
export function getRecipeById(id: string): ExampleRecipe | undefined {
  return EXAMPLE_RECIPES.find(r => r.id === id);
}
